import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { fromEvent, Observable, Subscription, throttleTime } from 'rxjs';

import { LightboxService } from './lightbox.service';
import { LightboxImage, LightboxImageToken } from './lightbox-image';
import { resizeImage } from './resize-image';
import { ViewportSize } from './viewport-size';

@Component({
  styleUrls: ['./lightbox.component.scss'],
  templateUrl: './lightbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeSlideInOut', [
      transition(':enter', [
        style({ transform: 'scale(0.25)' }),
        animate('150ms ease-in', style({ transform: 'scale(1)' }))
      ]),
      transition(':leave', [
        animate('100ms ease-out', style({ opacity: 0, transform: 'translateY(60px)' })),
      ]),
    ]),
  ]
})
export class LightboxComponent implements OnInit, OnDestroy {

  width: string;
  height: string;
  imageSource: SafeUrl;
  resize$: Observable<Event>;

  private subscription = new Subscription();
  @HostBinding('@fadeSlideInOut')
  private readonly animate = true;

  constructor(
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly sanitizer: DomSanitizer,
    public readonly lightboxService: LightboxService,
    @Inject(LightboxImageToken) public readonly imageParams: LightboxImage
  ) {
    this.updateSize();
    this.imageSource = sanitizer.bypassSecurityTrustUrl(imageParams.image);

    this.resize$ = zone.runOutsideAngular(() => fromEvent(window, 'resize').pipe(
      throttleTime(100)
    ));
  }

  ngOnInit() {
    this.subscription.add(this.resize$.subscribe(() => this.updateSize()));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.lightboxService.close();
  }

  private updateSize() {
    const viewportSize = new ViewportSize(window.innerWidth, window.innerHeight);
    const size = resizeImage(
      {
        width: this.imageParams.imageSize.naturalWidth,
        height: this.imageParams.imageSize.naturalHeight
      },
      viewportSize
    );
    this.width = size.width;
    this.height = size.height;

    this.cdr.markForCheck();
  }
}
